/**
 * As linhas de dados do papel — o que a loja pode reordenar (F160).
 *
 * O dono pediu para mudar a ordem das linhas no editor. A F149 tinha deixado a
 * ordem inteira em código, e a F160 abre **só esta parte**: as linhas
 * "rótulo … valor" do topo — número, data, cliente e os dados dele, vendedor,
 * operador, caixa.
 *
 * O resto continua em código, e o editor diz por quê: o aviso "RECIBO — SEM
 * VALOR FISCAL", a sequência itens → subtotal → total → pagamentos, e o código
 * de barras no fim. Pôr o total antes dos itens não é preferência de layout, é
 * um papel que se lê errado no balcão.
 *
 * Cada papel usa as linhas que tem: o recibo não tem "Válido até", o orçamento
 * não tem operador. Uma linha sem valor simplesmente não sai — nunca como
 * "Endereço: —".
 */
export const INFO_LINES = [
    'number',
    'date',
    'validUntil',
    'customer',
    'customerDocument',
    'customerPhone',
    'customerAddress',
    'customerEmail',
    'salesperson',
    'operator',
    'terminal',
];
/** O rótulo de cada linha, na tela do editor. */
export const INFO_LINE_LABELS = {
    number: 'Número do documento',
    date: 'Data',
    validUntil: 'Válido até (orçamento)',
    customer: 'Cliente',
    customerDocument: 'CPF/CNPJ do cliente',
    customerPhone: 'WhatsApp/telefone do cliente',
    customerAddress: 'Endereço do cliente',
    customerEmail: 'E-mail do cliente',
    salesperson: 'Vendedor',
    operator: 'Operador (cupom)',
    terminal: 'Caixa (cupom)',
};
/**
 * O que cabe numa linha da bobina estreita, e o teto do que se pode escrever.
 *
 * 32 colunas é o 58 mm, a bobina que a Elite Digital usa. Uma linha maior não
 * some: ela quebra em duas — é o que a frase do vale já faz desde a F126, com
 * 69 caracteres. Por isso a tela AVISA acima de `RECEIPT_NARROW_COLUMNS` em vez
 * de proibir, e o contrato só barra o exagero (`RECEIPT_LINE_MAX`).
 */
export const RECEIPT_NARROW_COLUMNS = 32;
export const RECEIPT_LINE_MAX = 120;
export const RECEIPT_FOOTER_MAX_LINES = 3;
export const RECEIPT_HEADER_MAX_LINES = 3;
const voucherNotice = 'O vale é crédito para compras nesta loja e não é trocado por dinheiro.';
export const DEFAULT_RECEIPT_TEMPLATE = {
    header: {
        showDocument: true,
        showAddress: true,
        showBranch: true,
        extraLines: [],
        showLegalName: false,
        showPostalCode: true,
        showPhone: true,
        showStateRegistration: false,
    },
    lines: {
        showUnitMath: true,
        showOverriddenMark: true,
        showCode: false,
        showLineDiscount: true,
        showLocation: false,
    },
    receipt: { showOperator: true, showTerminal: true },
    customer: {
        showAddress: true,
        showPhone: true,
        showDocument: false,
        showEmail: false,
        showCode: false,
        addressStyle: 'line',
    },
    quoteValidity: 'date',
    font: { family: 'consolas', size: 'normal', bold: true },
    /*
     * A ordem de hoje. Um papel sem dado de cliente sai caractere por caractere
     * igual ao de antes da F160 — é o que os testes de `receipt.test.ts` e
     * `quote.test.ts` continuam fixando.
     */
    infoOrder: INFO_LINES,
    documents: {
        receipt: {
            notice: 'RECIBO — SEM VALOR FISCAL',
            footerLines: ['Obrigado pela preferência!'],
            showBarcode: true,
            showDocumentId: true,
        },
        quote: {
            notice: 'não é comprovante de pagamento',
            footerLines: [],
            showBarcode: true,
            showDocumentId: true,
        },
        exchange: {
            notice: null,
            footerLines: [voucherNotice],
            showBarcode: true,
            showDocumentId: true,
        },
        voucher: {
            notice: null,
            footerLines: [voucherNotice],
            showBarcode: true,
            showDocumentId: true,
        },
        statement: {
            notice: null,
            footerLines: ['Este extrato não é recibo de pagamento.'],
            showBarcode: false,
            showDocumentId: false,
        },
        settlement: {
            notice: null,
            footerLines: [],
            showBarcode: false,
            showDocumentId: false,
        },
    },
    paper: 32,
};
export const templateOr = (template) => template ?? DEFAULT_RECEIPT_TEMPLATE;
/**
 * O nome do item no papel: com o código na frente, quando a loja pede (F161).
 * `008520 - CABO 2M TIPO C` é como o papel antigo escrevia, e é pelo código
 * que o balcão acha a peça na prateleira.
 */
export const itemName = (template, line) => template.lines.showCode &&
    line.code !== null &&
    line.code !== undefined &&
    line.code.trim() !== ''
    ? `${line.code} - ${line.description}`
    : line.description;
/**
 * A linha da localização, embaixo do nome — ou nula, e nada sai (26/09).
 *
 * Nula também com a chave ligada e a peça sem local cadastrado: uma linha
 * "Local:" vazia é papel gasto e uma pergunta a mais no balcão.
 */
export const itemLocation = (template, line) => {
    const location = line.location?.trim() ?? '';
    return template.lines.showLocation && location !== ''
        ? `  Local: ${location}`
        : null;
};
/**
 * A letra do modelo, para o documento levar até o desenho (F161). O padrão é
 * devolvido como `undefined`: o papel de quem nunca mexeu na fonte sai
 * idêntico ao de antes.
 */
export const styleOf = (template) => {
    const font = templateOr(template).font;
    const standard = DEFAULT_RECEIPT_TEMPLATE.font;
    return font.family === standard.family &&
        font.size === standard.size &&
        font.bold === standard.bold
        ? undefined
        : font;
};
export const documentTemplate = (template, key) => templateOr(template).documents[key];
export const headerBlocks = (input) => {
    const model = templateOr(input.template);
    const blocks = [
        { kind: 'text', text: input.company.name, align: 'center', bold: true },
    ];
    const present = (value) => value !== null && value !== undefined && value.trim() !== '';
    const { legalName, postalCode, phone, stateRegistration } = input.company;
    if (model.header.showLegalName &&
        present(legalName) &&
        legalName.trim() !== input.company.name.trim()) {
        blocks.push({ kind: 'text', text: legalName, align: 'center' });
    }
    if (model.header.showDocument && input.company.document !== null) {
        blocks.push({
            kind: 'text',
            text: `CNPJ ${input.company.document}`,
            align: 'center',
        });
    }
    if (model.header.showStateRegistration && present(stateRegistration)) {
        blocks.push({
            kind: 'text',
            text: `IE ${stateRegistration}`,
            align: 'center',
        });
    }
    if (model.header.showAddress && input.company.addressLine !== null) {
        blocks.push({
            kind: 'text',
            text: input.company.addressLine,
            align: 'center',
        });
    }
    if (model.header.showPostalCode && present(postalCode)) {
        blocks.push({ kind: 'text', text: `CEP ${postalCode}`, align: 'center' });
    }
    if (model.header.showPhone && present(phone)) {
        blocks.push({ kind: 'text', text: `Tel. ${phone}`, align: 'center' });
    }
    for (const line of model.header.extraLines) {
        if (line.trim() !== '') {
            blocks.push({ kind: 'text', text: line, align: 'center' });
        }
    }
    if (model.header.showBranch && input.branchName !== undefined) {
        blocks.push({ kind: 'text', text: input.branchName, align: 'center' });
    }
    return blocks;
};
/** As linhas finais que a loja escreveu, centralizadas como o resto. */
export const footerBlocks = (template, key) => documentTemplate(template, key)
    .footerLines.filter((line) => line.trim() !== '')
    .map((line) => ({ kind: 'text', text: line, align: 'center' }));
//# sourceMappingURL=receipt-template.js.map